import { useCallback, useSyncExternalStore } from 'react';

export type ConversationUiStatus = 'open' | 'closed';

export type ConversationUiState = {
  status: ConversationUiStatus;
  optedOut: boolean;
};

const DEFAULT_STATE: ConversationUiState = { status: 'open', optedOut: false };

/** In-memory UI-only conversation flags (not persisted to the API). */
const store = new Map<string, ConversationUiState>();
const listeners = new Set<() => void>();
let version = 0;

const subscribe = (onStoreChange: () => void) => {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
};

const getSnapshot = () => version;

const emitChange = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

const read = (conversationId: string): ConversationUiState => {
  const existing = store.get(conversationId);
  if (existing) return existing;
  store.set(conversationId, { ...DEFAULT_STATE });
  return store.get(conversationId)!;
};

const write = (conversationId: string, next: ConversationUiState) => {
  store.set(conversationId, next);
  emitChange();
};

/** True when message text is a contact STOP opt-out (inbound only — callers must check direction). */
export const isStopOptOutText = (text?: string | null) => (text ?? '').trim().toUpperCase() === 'STOP';

export const useConversationUiState = (conversationId: string) => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const live = read(conversationId);

  const setStatus = useCallback(
    (status: ConversationUiStatus) => {
      write(conversationId, { ...read(conversationId), status });
    },
    [conversationId]
  );

  const setOptedOut = useCallback(
    (optedOut: boolean) => {
      write(conversationId, { ...read(conversationId), optedOut });
    },
    [conversationId]
  );

  const markClosed = useCallback(() => setStatus('closed'), [setStatus]);
  const reopen = useCallback(() => setStatus('open'), [setStatus]);

  return {
    status: live.status,
    optedOut: live.optedOut,
    isOpen: live.status === 'open',
    isClosed: live.status === 'closed',
    setStatus,
    setOptedOut,
    markClosed,
    reopen,
  };
};

/** Test helper: clear in-memory UI state between cases. */
export const resetConversationUiState = () => {
  store.clear();
  emitChange();
};
